/**
 * useWatchlist.js - the viewer's My List from `/v1/me/state/watchlist`.
 */
import { useState, useEffect, useCallback } from 'react';
import { userDataService } from '../services/UserDataService';
import { useAuthContext } from '../contexts/AuthContext';

export const useWatchlist = () => {
  const { isAuthenticated } = useAuthContext();
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingItems, setRemovingItems] = useState(new Set());
  const [error, setError] = useState(null);

  const refreshWatchlist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setWatchlistData((await userDataService.refreshWatchlistData()) || []);
    } catch (err) {
      console.error('useWatchlist: Error refreshing watchlist data:', err);
      setError("We couldn't load your list right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshWatchlist();
    } else {
      setWatchlistData([]);
      setLoading(false);
      setError(null);
    }
  }, [isAuthenticated, refreshWatchlist]);

  const isInWatchlist = useCallback(
    (channelId, parentChannelId = null) =>
      userDataService.isInWatchlist(channelId, parentChannelId),
    [],
  );

  const getWatchlistCount = useCallback(() => userDataService.getWatchlistCount(), []);

  const addToWatchlist = useCallback(async (channelId, parentChannelId = null) => {
    const result = await userDataService.addToWatchlist(channelId, parentChannelId);
    if (result.success) {
      // The service re-read the list after the write.
      setWatchlistData(userDataService.watchlist);
    }
    return result;
  }, []);

  const removeFromWatchlist = useCallback(
    async channelId => {
      setRemovingItems(prev => new Set([...prev, channelId]));
      // Optimistic: drop it now, restore from the server if the write did not land.
      setWatchlistData(prev => prev.filter(item => item._id !== channelId));
      try {
        const result = await userDataService.removeFromWatchlist(channelId);
        if (!result.success) {
          await refreshWatchlist();
        }
        return result;
      } finally {
        setRemovingItems(prev => {
          const next = new Set(prev);
          next.delete(channelId);
          return next;
        });
      }
    },
    [refreshWatchlist],
  );

  const isRemoving = useCallback(channelId => removingItems.has(channelId), [removingItems]);

  return {
    watchlistData,
    loading,
    removingItems,
    isRemoving,
    error,
    refreshWatchlist,
    isInWatchlist,
    getWatchlistCount,
    addToWatchlist,
    removeFromWatchlist,
    hasWatchlist: watchlistData.length > 0,
  };
};
